import type { Findings, Severity } from './api'

/**
 * The canvas's hierarchy badges: per object, the highest severity and how many findings name it
 * (`byObject` from GET /findings), shown only while the advisor panel's review is loaded.
 *
 * Held OUTSIDE the nodes array, like utils/connectionValidity.js and utils/focusMode.js. Writing
 * the counts into node.data would change the nodes array, re-run the ELK layout memo and need a
 * new field in CustomNodes' propsAreEqual; here only the badge subscribes (useSyncExternalStore),
 * so a review loading or closing repaints the pills and nothing else (29 Sep 2026).
 */

export type BadgeEntry = { severity: Severity; count: number }

let byObject: Record<string, BadgeEntry> = {}
let version = 0
const listeners = new Set<() => void>()

function emit() {
    version += 1
    listeners.forEach((l) => l())
}

export function subscribeHierarchyBadges(listener: () => void) {
    listeners.add(listener)
    return () => {
        listeners.delete(listener)
    }
}

export function getHierarchyBadgesVersion() {
    return version
}

/** Publish a loaded review's badges. Anything not in the map shows no badge. */
export function setHierarchyBadges(map: Findings['byObject'] | null | undefined) {
    byObject = map ? { ...map } : {}
    emit()
}

/** The panel closed (or its review went away): no badges. */
export function clearHierarchyBadges() {
    if (Object.keys(byObject).length === 0) return
    byObject = {}
    emit()
}

/**
 * The badge for a node. A canvas node's id, the ObjectID on its data and the lower-cased spelling
 * are used in different parts of this canvas (see assignKey in CustomNodes), so each is tried.
 */
export function hierarchyBadgeFor(ids: ReadonlyArray<string | number | null | undefined>): BadgeEntry | null {
    for (const id of ids) {
        if (id === null || id === undefined || id === '') continue
        const hit = byObject[String(id)]
        if (hit && hit.count > 0) return hit
    }
    return null
}

/** The pill colours, by the highest severity - the panel's severity dots use the same. */
export const SEVERITY_COLOUR: Record<Severity, string> = {
    high: '#DC2626',
    medium: '#D97706',
    low: '#2563EB',
}
